import { Button as AntButton, Popover, Radio } from "antd";
import type { RadioChangeEvent } from "antd/es/radio";
import { Search } from "../../shared/layout/Search";
import { formatTranslation } from "../../shared/utils/translationUtils";
import { useTranslation } from "react-i18next";
import filterIcon from "../../assets/images/icon-filter.svg";

/**
 * Renders the filter panel content for the completion status filter.
 * This includes radio buttons for selecting the filter and action buttons to apply or reset the filter.
 */
const FilterPanel = (props: {
  completionFilter: string;
  setCompletionFilter: (filter: string) => void;
  applyFilter: (reset?: boolean) => void;
  t: any;
}): JSX.Element => {
  const { completionFilter, setCompletionFilter, applyFilter, t } = props;
  const radioGroupStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  };
  return (
    <div style={{ padding: 12, minWidth: 200 }}>
      <Radio.Group
        onChange={(e: RadioChangeEvent) => {
          setCompletionFilter(e.target.value);
        }}
        style={radioGroupStyle}
        value={completionFilter}
      >
        <Radio value="all">
          {formatTranslation(t("trialFilter.allStatuses"))}
        </Radio>
        <Radio value="completed">
          {formatTranslation(t("trialFilter.completed"))}
        </Radio>
        <Radio value="incomplete">
          {formatTranslation(t("trialFilter.incomplete"))}
        </Radio>
      </Radio.Group>
      <div style={{ margin: "12px 0" }} />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <AntButton
          type="link"
          onClick={() => {
            applyFilter(true);
          }}
        >
          {formatTranslation(t("protocolFilter.clear"))}
        </AntButton>
        <AntButton
          type="primary"
          onClick={() => {
            applyFilter(false);
          }}
        >
          {formatTranslation(t("protocolFilter.apply"))}
        </AntButton>
      </div>
    </div>
  );
};

/**
 * this component renders the search and filter section for the trials page.
 * It includes a search input and a filter dropdown for filtering trials based on their completion status.
 * @param param0 Props for the component, including search text, completion filter, and functions to update them.
 * @returns JSX element for the search and filter section.
 */
const SearchAndFilter = ({
  searchText,
  setSearchText,
  completionFilter,
  setCompletionFilter,
  applyFilter,
  dropdownVisible,
  setDropdownVisible,
}: {
  searchText: string;
  setSearchText: (text: string) => void;
  completionFilter: string;
  setCompletionFilter: (filter: string) => void;
  applyFilter: (reset?: boolean) => void;
  dropdownVisible: boolean;
  setDropdownVisible: (visible: boolean) => void;
}): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className="search-sec">
      <h3>{formatTranslation(t("trials.description"))}</h3>
      <div className="search-right">
        <Search searchText={searchText} setSearchTexts={setSearchText} />
        <Popover
          content={
            <FilterPanel
              completionFilter={completionFilter}
              setCompletionFilter={setCompletionFilter}
              applyFilter={applyFilter}
              t={t}
            />
          }
          trigger="click"
          open={dropdownVisible}
          onOpenChange={(visible) => {
            setDropdownVisible(visible);
            if (visible) {
              setCompletionFilter(completionFilter);
            }
          }}
          placement="bottomRight"
          classNames={{ root: "protocol-filter-popover" }}
        >
          <AntButton
            aria-label="Filter"
            icon={<img src={filterIcon} alt="" aria-hidden="true" />}
          />
        </Popover>
      </div>
    </div>
  );
};

export default SearchAndFilter;
